// The three things a game offers wherever it is shown, and the pictures that
// stand for them: Play, Download, Remix. The landing page's shelf, a game's
// page and the play page's dock draw the same ones (the web player's own
// fallback dock, in PocketJS, carries copies of `download` and `remix`).
//
// And the devices a game runs on, each a small picture beside its name on a
// card of the landing page's shelf (devicesHtml in ./offer.ts).
//
// Each is the inside of an `<svg viewBox="0 0 24 24">`. Nothing here imports
// the DOM: the server writes them into a page and the pages' scripts into
// their buttons.

/** A drawing's strokes: the page's text colour, round ends. */
const LINE = 'fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"';

export const ICON_PATHS = {
  /** A triangle that points right. */
  play: '<path d="M8 5.2v13.6a.9.9 0 0 0 1.37.77l11-6.8a.9.9 0 0 0 0-1.54l-11-6.8A.9.9 0 0 0 8 5.2z" fill="currentColor"/>',
  /** An arrow into a tray. */
  download: `<path ${LINE} d="M12 4v11M7.5 10.5 12 15l4.5-4.5M5 20h14"/>`,
  /** One line that comes from two: a game started from another. */
  remix: `<g ${LINE}><circle cx="7" cy="6" r="2.2"/><circle cx="17" cy="6" r="2.2"/><circle cx="12" cy="18" r="2.2"/><path d="M7 8.2v1.3a3 3 0 0 0 3 3h4a3 3 0 0 0 3-3V8.2M12 12.5v3.3"/></g>`,
  /** Two angle brackets: source code. */
  code: `<path ${LINE} d="M8.5 7 3.5 12l5 5M15.5 7l5 5-5 5"/>`,
  /** A clock's face with an arrow round it: the versions before this one. */
  versions: `<g ${LINE}><path d="M4.2 12a7.8 7.8 0 1 0 2.4-5.6L4 9"/><path d="M4 4.6V9h4.4M12 8v4.4l2.8 1.8"/></g>`,
  /** A padlock: what the membership opens. */
  lock: `<g ${LINE}><rect x="5.5" y="10.5" width="13" height="9.5" rx="2.4"/><path d="M8.5 10.5V8a3.5 3.5 0 0 1 7 0v2.5"/></g>`,
  /** An arrow that leaves the page. */
  out: `<path ${LINE} d="M9 6h9v9M18 6 7 17"/>`,
  /** A cross: close. */
  close: `<path ${LINE} stroke-width="3" d="M6 6l12 12M18 6 6 18"/>`,
  /** An arrow to the next game, and to the one before it. */
  next: `<path ${LINE} d="M9 5l7 7-7 7"/>`,
  previous: `<path ${LINE} d="M15 5l-7 7 7 7"/>`,
  // The devices, by their ids. Each is the device's outline with its screen filled in.
  /** A wide handheld with round ends. */
  psp: `<rect ${LINE} x="1.8" y="6.8" width="20.4" height="10.4" rx="5.2"/><rect x="7.6" y="9.6" width="8.8" height="4.8" rx="1" fill="currentColor"/>`,
  /** The same shape with a stick on each side of the screen. */
  vita: `<rect ${LINE} x="1.8" y="6.8" width="20.4" height="10.4" rx="5.2"/><rect x="8.4" y="9.6" width="7.2" height="4.8" rx="1" fill="currentColor"/><circle cx="5.6" cy="12" r="1.25" fill="currentColor"/><circle cx="18.4" cy="12" r="1.25" fill="currentColor"/>`,
  /** Two halves, a screen in each: the upper one wide. */
  "3ds": `<g ${LINE}><rect x="3.8" y="2.8" width="16.4" height="8.6" rx="2"/><rect x="3.8" y="12.6" width="16.4" height="8.6" rx="2"/></g><rect x="6.6" y="5.2" width="10.8" height="3.8" rx=".8" fill="currentColor"/><rect x="8.6" y="15" width="6.8" height="3.8" rx=".8" fill="currentColor"/>`,
  /** A music player held upright, with its one round button under the screen. */
  "ipod-touch": `<rect ${LINE} x="6.4" y="2.2" width="11.2" height="19.6" rx="2.8"/><circle cx="12" cy="18.4" r="1.35" fill="currentColor"/><path ${LINE} stroke-width="1.8" d="M9.4 15.2h5.2"/>`,
  /** A phone held upright: the screen from edge to edge and a bar to swipe at its foot. */
  android: `<rect ${LINE} x="6.4" y="2.2" width="11.2" height="19.6" rx="2.8"/><path ${LINE} d="M10 18.2h4"/>`,
  /** A screen on a stand. */
  desktop: `<g ${LINE}><rect x="2.8" y="4.2" width="18.4" height="12" rx="2.2"/><path d="M8.6 20.4h6.8M12 16.2v4.2"/></g>`,
} as const;

export type IconName = keyof typeof ICON_PATHS;

/** An icon as an SVG element's text, `size` pixels square, hidden from a reader: the control it is in says what it does. */
export function icon(name: IconName, size = 22): string {
  return `<svg viewBox="0 0 24 24" width="${size}" height="${size}" aria-hidden="true" focusable="false">${ICON_PATHS[name]}</svg>`;
}
